/**
 * Trovva Intelligence Dashboard Cards
 *
 * Four cards that surface intelligence on the broker dashboard:
 *   1. Deal Risk Monitor — deals color-coded by risk score
 *   2. Compliance Alerts — active issues sorted by severity
 *   3. Market Pulse — anonymized cross-brokerage benchmarks
 *   4. Pipeline Prediction — predicted close rate + revenue
 */
import { useState, useEffect } from 'react';
import {
  AlertTriangle, Shield, TrendingUp, BarChart3, ChevronRight,
  CheckCircle, XCircle, AlertCircle, Clock, ArrowUp, ArrowDown,
  X, Eye, Loader2,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

// ── Shared styles ───────────────────────────────────────────
const cardOuter = {
  background: '#fff', borderRadius: 14, padding: 20,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  border: `1px solid ${theme.colors.border}`,
};
const sectionTitle = (icon, title, color) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}12`,
    }}>
      {icon}
    </div>
    <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>{title}</span>
  </div>
);

const RISK_COLORS = {
  strong: '#16A34A',
  on_track: '#2563EB',
  at_risk: '#D97706',
  critical: '#DC2626',
};
const RISK_LABELS = {
  strong: 'Strong',
  on_track: 'On Track',
  at_risk: 'At Risk',
  critical: 'Critical',
};
const SEVERITY_COLORS = {
  critical: '#DC2626',
  warning: '#D97706',
  info: '#2563EB',
};

// ── Main Component ──────────────────────────────────────────
export default function IntelligenceCards({ demoMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (demoMode) {
      setData(DEMO_DATA);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setLoading(false); return; }
        const resp = await fetch('/api/intelligence?type=dashboard', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (resp.ok) {
          setData(await resp.json());
        }
      } catch (e) { setError(e.message); }
      setLoading(false);
    })();
  }, [demoMode]);

  if (loading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ ...cardOuter, minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 size={20} style={{ color: theme.colors.textLight, animation: 'spin 1s linear infinite' }} />
          </div>
        ))}
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.accent}15)`,
        }}>
          <BarChart3 size={16} style={{ color: theme.colors.primary }} />
        </div>
        <span style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Trovva Intelligence</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: theme.colors.accent, background: `${theme.colors.accent}12`, padding: '2px 8px', borderRadius: 6 }}>
          LIVE
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <DealRiskCard scores={data.dealScores} />
        <ComplianceCard issues={data.complianceIssues} alerts={data.alerts} />
        <PipelineCard prediction={data.pipelinePrediction} />
        <MarketCard demoMode={demoMode} />
      </div>
    </div>
  );
}

// ── 1. Deal Risk Monitor ────────────────────────────────────
function DealRiskCard({ scores }) {
  const sorted = [...(scores || [])].sort((a, b) => a.score - b.score);
  const riskCounts = {
    critical: sorted.filter(s => s.risk_level === 'critical').length,
    at_risk: sorted.filter(s => s.risk_level === 'at_risk').length,
    on_track: sorted.filter(s => s.risk_level === 'on_track').length,
    strong: sorted.filter(s => s.risk_level === 'strong').length,
  };

  return (
    <div style={cardOuter}>
      {sectionTitle(
        <AlertTriangle size={16} style={{ color: '#D97706' }} />,
        'Deal Risk Monitor',
        '#D97706'
      )}

      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 13, color: theme.colors.textLight }}>No active deals to score yet</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>Deal risk scores appear as you add transactions</div>
        </div>
      ) : (
        <>
          {/* Risk summary bar */}
          <div style={{ display: 'flex', gap: 2, height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 14 }}>
            {['critical', 'at_risk', 'on_track', 'strong'].map(level => (
              riskCounts[level] > 0 ? (
                <div key={level} style={{
                  flex: riskCounts[level],
                  background: RISK_COLORS[level],
                  transition: 'flex 0.3s',
                }} />
              ) : null
            ))}
          </div>

          {/* Risk counts */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            {['critical', 'at_risk', 'on_track', 'strong'].map(level => (
              <div key={level} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: RISK_COLORS[level] }}>{riskCounts[level]}</div>
                <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>{RISK_LABELS[level]}</div>
              </div>
            ))}
          </div>

          {/* Top risk deals */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sorted.slice(0, 3).map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                borderRadius: 8, background: '#F8FAFC', fontSize: 13,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${RISK_COLORS[s.risk_level]}15`, color: RISK_COLORS[s.risk_level],
                  fontWeight: 700, fontSize: 12,
                }}>{s.score}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.deals?.property_address || 'Unnamed deal'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                    {s.signals?.[0]?.explanation || s.risk_level}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── 2. Compliance Alerts ────────────────────────────────────
function ComplianceCard({ issues, alerts }) {
  const activeIssues = issues || [];
  const criticalCount = activeIssues.filter(i => i.severity === 'critical').length;
  const warningCount = activeIssues.filter(i => i.severity === 'warning').length;

  return (
    <div style={{
      ...cardOuter,
      borderColor: criticalCount > 0 ? '#FCA5A5' : warningCount > 0 ? '#FDE68A' : theme.colors.border,
    }}>
      {sectionTitle(
        <Shield size={16} style={{ color: criticalCount > 0 ? '#DC2626' : '#10B981' }} />,
        'Compliance Autopilot',
        criticalCount > 0 ? '#DC2626' : '#10B981'
      )}

      {activeIssues.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <CheckCircle size={28} style={{ color: '#16A34A', marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#15803D' }}>All clear</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>
            No compliance issues detected across {15} NJ rules
          </div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            {criticalCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, background: '#FEF2F2' }}>
                <XCircle size={14} style={{ color: '#DC2626' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#DC2626' }}>{criticalCount} critical</span>
              </div>
            )}
            {warningCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, background: '#FFFBEB' }}>
                <AlertCircle size={14} style={{ color: '#D97706' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#D97706' }}>{warningCount} warning</span>
              </div>
            )}
          </div>

          {/* Issue list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activeIssues.slice(0, 4).map((issue, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
                borderRadius: 8, background: issue.severity === 'critical' ? '#FEF2F2' : '#FFFBEB',
                fontSize: 12,
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                  background: SEVERITY_COLORS[issue.severity],
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: 13 }}>{issue.rule_id}</div>
                  <div style={{ color: theme.colors.textLight, lineHeight: 1.4 }}>{issue.details}</div>
                </div>
              </div>
            ))}
          </div>

          {activeIssues.length > 4 && (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <span style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 500, cursor: 'pointer' }}>
                View all {activeIssues.length} issues →
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── 3. Pipeline Prediction ──────────────────────────────────
function PipelineCard({ prediction }) {
  const p = prediction || {};

  return (
    <div style={cardOuter}>
      {sectionTitle(
        <TrendingUp size={16} style={{ color: '#7C3AED' }} />,
        'Pipeline Prediction',
        '#7C3AED'
      )}

      {p.totalDeals === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 13, color: theme.colors.textLight }}>Add deals to see pipeline predictions</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>AI scores each deal's likelihood of closing</div>
        </div>
      ) : (
        <>
          {/* Big number — predicted revenue */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: theme.colors.primary }}>
              ${(p.predictedRevenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div style={{ fontSize: 12, color: theme.colors.textLight, fontWeight: 500 }}>Predicted commission revenue</div>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div style={{ textAlign: 'center', padding: '10px 0', borderRadius: 8, background: '#F8FAFC' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text }}>{p.totalDeals}</div>
              <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>Active Deals</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 0', borderRadius: 8, background: '#F0FDF4' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#16A34A' }}>{p.strongDeals}</div>
              <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>Likely to Close</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 0', borderRadius: 8, background: '#FEF2F2' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#DC2626' }}>{p.atRiskDeals}</div>
              <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>At Risk</div>
            </div>
          </div>

          {/* Avg score */}
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <span style={{ fontSize: 12, color: theme.colors.textLight }}>
              Avg pipeline health score: <strong style={{ color: p.avgScore >= 70 ? '#16A34A' : p.avgScore >= 50 ? '#D97706' : '#DC2626' }}>{p.avgScore}/100</strong>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── 4. Market Pulse ─────────────────────────────────────────
function MarketCard({ demoMode }) {
  const [market, setMarket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (demoMode) {
      setMarket(DEMO_MARKET);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setLoading(false); return; }
        const resp = await fetch('/api/intelligence?type=market', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (resp.ok) setMarket(await resp.json());
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [demoMode]);

  return (
    <div style={cardOuter}>
      {sectionTitle(
        <BarChart3 size={16} style={{ color: '#0EA5E9' }} />,
        'Market Pulse',
        '#0EA5E9'
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Loader2 size={18} style={{ color: theme.colors.textLight, animation: 'spin 1s linear infinite' }} />
        </div>
      ) : !market?.hasData ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <BarChart3 size={28} style={{ color: theme.colors.textLight, marginBottom: 8 }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Building your market intelligence</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4, lineHeight: 1.5 }}>
            As more brokerages join Trovva, anonymized benchmarks will appear here — average days on market, price trends, and lead source performance for your area.
          </div>
          <div style={{
            marginTop: 12, padding: '6px 12px', borderRadius: 8,
            background: `${theme.colors.primary}08`, border: `1px solid ${theme.colors.primary}20`,
            fontSize: 11, color: theme.colors.primary, fontWeight: 500,
          }}>
            The more brokerages on Trovva, the smarter everyone gets
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MetricBox
              label="Avg Days on Market"
              value={market.current.avgDaysOnMarket ? `${Math.round(market.current.avgDaysOnMarket)}` : '—'}
              trend={market.trend?.daysOnMarketChange}
              invertTrend // lower is better
            />
            <MetricBox
              label="List-to-Sale Ratio"
              value={market.current.listToSaleRatio ? `${(market.current.listToSaleRatio * 100).toFixed(1)}%` : '—'}
            />
            <MetricBox
              label="Avg Sale Price"
              value={market.current.avgSalePrice ? `$${Math.round(market.current.avgSalePrice / 1000)}K` : '—'}
              trend={market.trend?.priceChange ? `${market.trend.priceChange}%` : null}
            />
            <MetricBox
              label="Deal Volume"
              value={market.current.dealCount || '—'}
              trend={market.trend?.volumeChange}
            />
          </div>

          {!market.meetsThreshold && (
            <div style={{ marginTop: 10, fontSize: 11, color: theme.colors.textLight, textAlign: 'center' }}>
              ⓘ Minimum 3 brokerages needed for full benchmarks
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetricBox({ label, value, trend, invertTrend }) {
  const isPositive = invertTrend ? (typeof trend === 'number' ? trend < 0 : false) : (typeof trend === 'number' ? trend > 0 : false);
  const trendStr = typeof trend === 'number' ? (trend > 0 ? `+${trend}` : `${trend}`) : trend;

  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, background: '#F8FAFC' }}>
      <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>{value}</span>
        {trendStr && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            color: isPositive ? '#16A34A' : '#DC2626',
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            {isPositive ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
            {trendStr}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Demo Data ───────────────────────────────────────────────
const DEMO_DATA = {
  dealScores: [
    { score: 28, risk_level: 'critical', signals: [{ explanation: 'No activity for 35 days' }], deals: { property_address: '42 Elm St, Montclair' } },
    { score: 52, risk_level: 'at_risk', signals: [{ explanation: 'Inspection failed — renegotiation likely' }], deals: { property_address: '18 Oak Ave, Bloomfield' } },
    { score: 71, risk_level: 'on_track', signals: [{ explanation: 'Attorney review completed' }], deals: { property_address: '305 Valley Rd, Wayne' } },
    { score: 88, risk_level: 'strong', signals: [{ explanation: 'Cash buyer, closing in 5 days' }], deals: { property_address: '7 Park Pl, Glen Ridge' } },
    { score: 91, risk_level: 'strong', signals: [{ explanation: 'Financing committed' }], deals: { property_address: '120 Broad St, Newark' } },
  ],
  complianceIssues: [
    { rule_id: 'NJ-001', severity: 'critical', details: 'Property built in 1965 — lead paint disclosure required but not found', category: 'disclosure' },
    { rule_id: 'NJ-003', severity: 'critical', details: 'Seller Disclosure Statement not found — required before closing', category: 'document' },
    { rule_id: 'NJ-009', severity: 'warning', details: 'Radon disclosure not found — required for NJ residential sales', category: 'disclosure' },
  ],
  alerts: [],
  pipelinePrediction: {
    totalDeals: 5,
    avgScore: 66,
    predictedRevenue: 47250,
    strongDeals: 2,
    atRiskDeals: 2,
  },
};

const DEMO_MARKET = {
  hasData: true,
  meetsThreshold: true,
  current: {
    avgDaysOnMarket: 34,
    medianDaysOnMarket: 28,
    listToSaleRatio: 0.975,
    avgListPrice: 485000,
    avgSalePrice: 472875,
    dealCount: 23,
    avgSpeedToLead: 180,
    leadToDealRate: 0.042,
  },
  trend: {
    daysOnMarketChange: -3,
    priceChange: '2.1',
    volumeChange: 4,
  },
};
